// Мок голосовой сессии — заглушка вместо реального бэкенда. Позволяет проверить
// весь UI (транскрипция, потоковый ответ, воспроизведение PCM, прерывание,
// статус агента) до готовности сервера. Включается через VITE_USE_MOCK=true.

import { config } from '../config'
import type {
  AgentStatus,
  ConnectionState,
  SessionEvent,
  SessionEventHandler,
  VoiceSession,
} from '../types'

const REPLIES = [
  'Привет! Я мок-агент. Сервер ещё не подключён, но интерфейс уже работает.',
  'Понял задачу. Сейчас распределю её между субагентами и вернусь с результатом.',
  'Готово. Это тестовый ответ — здесь будет настоящая речь агента с VPS.',
  'Слышу тебя. Распознавание речи на бэкенде заменит эту заглушку.',
]

export class MockVoiceSession implements VoiceSession {
  private handlers = new Set<SessionEventHandler>()
  private _state: ConnectionState = 'idle'
  private timers: number[] = []
  private replyIdx = 0
  private speaking = false
  private framesSinceSpeech = 0
  private partialTimer: number | null = null
  private statusTimer: number | null = null

  get state(): ConnectionState {
    return this._state
  }

  on(handler: SessionEventHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  private emit(e: SessionEvent): void {
    this.handlers.forEach((h) => h(e))
  }

  private setState(state: ConnectionState): void {
    this._state = state
    this.emit({ type: 'connection', state })
  }

  private later(fn: () => void, ms: number): void {
    this.timers.push(window.setTimeout(fn, ms))
  }

  connect(): void {
    this.setState('connecting')
    this.later(() => {
      this.setState('connected')
      this.startStatusLoop()
    }, 400)
  }

  disconnect(): void {
    this.timers.forEach(clearTimeout)
    this.timers = []
    if (this.partialTimer) clearTimeout(this.partialTimer)
    if (this.statusTimer) clearTimeout(this.statusTimer)
    this.partialTimer = this.statusTimer = null
    this.setState('closed')
  }

  // Имитируем распознавание речи по входящим аудио-кадрам.
  sendAudio(_pcm: Int16Array): void {
    this.framesSinceSpeech++
    if (this.partialTimer) return
    const words = ['так', 'мне', 'нужно', 'проверить', 'голосовой', 'интерфейс']
    let i = 0
    const tick = () => {
      i++
      this.emit({
        type: 'user_transcript',
        text: words.slice(0, i).join(' '),
        final: false,
      })
      if (i < words.length) {
        this.partialTimer = window.setTimeout(tick, 350)
      } else {
        this.partialTimer = null
        this.emit({ type: 'user_transcript', text: words.join(' '), final: true })
        this.respond()
      }
    }
    this.partialTimer = window.setTimeout(tick, 350)
  }

  sendText(text: string): void {
    const trimmed = text.trim()
    if (!trimmed) return
    this.emit({ type: 'user_transcript', text: trimmed, final: true })
    this.respond()
  }

  interrupt(): void {
    if (!this.speaking) return
    this.timers.forEach(clearTimeout)
    this.timers = []
    this.speaking = false
    this.emit({ type: 'interrupted' })
    this.emit({ type: 'agent_speech_end' })
  }

  private respond(): void {
    const reply = REPLIES[this.replyIdx % REPLIES.length]
    this.replyIdx++
    this.speaking = true
    this.framesSinceSpeech = 0

    this.emit({ type: 'agent_speech_start' })
    this.emit({ type: 'agent_status', status: mkStatus('working', 'Формирую ответ', 0.2) })

    // Потоковый текст по словам. Аудио мок НЕ присылает — в режиме TTS 'auto'
    // приложение само озвучит финальный текст локальным синтезом (реальная речь).
    // Когда появится бэкенд со своим TTS, он будет слать события agent_audio.
    const words = reply.split(' ')
    let i = 0
    const step = () => {
      if (!this.speaking) return
      i++
      this.emit({ type: 'agent_text', text: words.slice(0, i).join(' '), final: i >= words.length })
      if (i < words.length) {
        this.later(step, 180)
      } else {
        this.speaking = false
        this.emit({ type: 'agent_speech_end' })
        this.emit({ type: 'agent_status', status: mkStatus('done', 'Ответ отправлен', 1) })
      }
    }
    this.later(step, 200)
  }

  private startStatusLoop(): void {
    const states: AgentStatus['state'][] = ['idle', 'waiting', 'working', 'done']
    let k = 0
    const loop = () => {
      const st = states[k % states.length]
      k++
      this.emit({
        type: 'agent_status',
        status: mkStatus(st, st === 'idle' ? 'Ожидание' : 'Фоновая задача #' + k, (k % 5) / 4),
      })
      this.statusTimer = window.setTimeout(loop, config.agentStatusPollMs)
    }
    loop()
  }
}

function mkStatus(state: AgentStatus['state'], task: string, progress: number): AgentStatus {
  return { state, task, progress, lastResult: 'мок-результат', updatedAt: Date.now() }
}
